
import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Session } from '@supabase/supabase-js';

interface AuthContextType {
  session: Session | null;
  isAdmin: boolean;
  isLoading: boolean;
  authError: Error | null;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);

  const checkAdminStatus = async (currentSession?: Session | null) => {
    try {
      const activeSession = currentSession || session;
      if (!activeSession?.user) {
        setIsAdmin(false);
        return;
      }

      // Check user metadata first (faster)
      const role = activeSession.user.user_metadata?.role;
      if (role === 'admin') {
        setIsAdmin(true);
        setAuthError(null);
        return;
      }

      // Use the new admin check function
      const { data, error } = await supabase.rpc('is_user_admin');
      if (error) {
        console.error('Admin check failed:', error);
        setIsAdmin(false);
        setAuthError(error as Error);
        return;
      }

      const adminStatus = !!data;
      setIsAdmin(adminStatus);
      setAuthError(null);

    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
      setAuthError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSession = async () => {
    try {
      const { data: { session: newSession }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        throw error;
      }

      setSession(newSession);
      if (newSession?.user) {
        await checkAdminStatus(newSession);
      }
    } catch (error) {
      console.error('Session refresh failed:', error);
      setAuthError(error as Error);
    }
  };

  useEffect(() => {
    let mounted = true;
    
    const setupAuth = async () => {
      setIsLoading(true);
      
      // Set up auth state listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, newSession) => {
          if (!mounted) return;
          
          console.log('Auth event:', event, newSession?.user?.id);
          
          setSession(newSession);
          
          if (event === 'SIGNED_IN' && newSession) {
            await checkAdminStatus(newSession);
          } else if (event === 'SIGNED_OUT') {
            setIsAdmin(false);
            setAuthError(null);
          }
          
          setIsLoading(false);
        }
      );
      
      // Check for existing session
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      
      if (mounted) {
        setSession(existingSession);
        
        if (existingSession?.user) {
          await checkAdminStatus(existingSession);
        } else {
          setIsLoading(false);
        }
      }
      
      return () => {
        subscription.unsubscribe();
      };
    };
    
    setupAuth();
    
    return () => {
      mounted = false;
    };
  }, []);

  const signOut = async () => {
    try {
      setIsLoading(true);

      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }

      // Clear local state
      setSession(null);
      setIsAdmin(false);
      setAuthError(null);
    } catch (error) {
      console.error("Error signing out:", error);
      setAuthError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    session,
    isAdmin,
    isLoading,
    authError,
    signOut,
    refreshSession
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
